/* ============================================================
   src/shared/completeness.ts
   ============================================================
   "Did I forget something?" check for an estimate.

   Jay built an estimate on the phone and moved on to the proposal
   without ever adding railing — nothing in the app said a word,
   because every scope category is legitimately optional (plenty of
   jobs have no demo, no stairs, no skirting). So instead of blocking
   the proposal, both shells run this check at the point where the
   user leaves the estimator (desktop "Generate proposal" / Proposal
   nav, mobile "Review & Send") and make the user ACKNOWLEDGE the
   blank categories before continuing.

   Two kinds of gap are reported:
     - "empty"   nothing entered for the category at all
     - "partial" a type with no quantity, or a quantity with no type
                 (the more dangerous one — it prices as zero)

   A category covered by an ADD ITEM row is not reported: someone who
   priced railing as an add-item did not forget railing.

   Add Items rows are checked too: a row with something picked (Door →
   Slider) that comes out to $0 — usually a blank quantity — is reported
   as "partial". That needs the PRICED rows (lineBase), so callers pass
   computeEstimate's addItemsDetailed as addItems.

   Pure + framework-agnostic so desktop (App.tsx state) and mobile
   (DraftSnapshot) can both feed it, and so it is unit-testable.
   ============================================================ */

import { normalizeCat, type AddItemRow } from "./pricing";

export type SectionKey =
  | "constructionType"
  | "decking"
  | "railing"
  | "stairs"
  | "demo"
  | "fasteners"
  | "skirting";

export type MissingSection = {
  /** A scope category, or `addItem:<rowId>` for one Add Items row. */
  key: SectionKey | `addItem:${string}`;
  /** Card/section name as it reads in the UI. */
  label: string;
  /** "empty" = untouched; "partial" = half-filled, so it prices as zero. */
  kind: "empty" | "partial";
  /** One-line explanation shown under the label in the alert. */
  detail: string;
};

/** The subset of an estimate this check reads. Both App.tsx's local state
 *  and a DraftSnapshot satisfy it structurally. */
export type CompletenessInput = {
  constructionType?: string | null;
  selectedDeckingId?: string | null;
  deckingSqFt?: number | null;
  selectedRailingId?: string | null;
  railingLf?: number | null;
  selectedStairsId?: string | null;
  stairsCount?: number | null;
  selectedFastenerId?: string | null;
  selectedDemoId?: string | null;
  demoQty?: number | null;
  skirtingCategory?: string | null;
  selectedSkirtingId?: string | null;
  skirtingSf?: number | null;
  /** Pass the priced rows (addItemsDetailed) so $0 rows can be caught;
   *  plain rows still drive the "covered by an add-item" rule. */
  addItems?: (AddItemRow & { lineBase?: number; displayName?: string })[] | null;
};

const has = (v: unknown) => !!String(v ?? "").trim();
const qty = (v: unknown) => Number(v ?? 0) > 0;

/** Add-item category name fragments that mean "this scope category is
 *  already priced as an add-item". Matched against the normalized
 *  category (lowercased, spaces/dashes → underscore), so "Railing
 *  Options" and "railing_options" both hit. */
const ADD_ITEM_KEYWORDS: Record<SectionKey, string[]> = {
  constructionType: ["construction"],
  decking: ["decking"],
  railing: ["railing"],
  stairs: ["stair"],
  demo: ["demo"],
  fasteners: ["fastener"],
  skirting: ["skirting", "lattice"],
};

/** True when some add-item row both names this category AND actually
 *  picked something (a catalog item or a custom line). An empty row the
 *  user opened and abandoned must not silence the warning. */
function coveredByAddItem(
  addItems: AddItemRow[] | null | undefined,
  key: SectionKey
): boolean {
  const words = ADD_ITEM_KEYWORDS[key] || [];
  return (addItems || []).some((row) => {
    const cat = normalizeCat(row?.category || "");
    if (!cat) return false;
    if (!words.some((w) => cat.includes(w))) return false;
    return has(row?.itemId) || has(row?.customName);
  });
}

/** A category needing BOTH a type and a quantity. */
function checkPair(
  key: SectionKey,
  label: string,
  typePicked: boolean,
  qtyEntered: boolean,
  typeNoun: string,
  qtyNoun: string
): MissingSection | null {
  if (typePicked && qtyEntered) return null;
  if (!typePicked && !qtyEntered) {
    return {
      key,
      label,
      kind: "empty",
      detail: `No ${typeNoun} and no ${qtyNoun} entered.`,
    };
  }
  if (typePicked && !qtyEntered) {
    return {
      key,
      label,
      kind: "partial",
      detail: `A ${typeNoun} is selected but the ${qtyNoun} is blank — it prices as $0.`,
    };
  }
  return {
    key,
    label,
    kind: "partial",
    detail: `A ${qtyNoun} is entered but no ${typeNoun} is selected — it prices as $0.`,
  };
}

/**
 * Blank / half-filled scope categories, in the order they appear in the
 * estimator. Empty array = nothing to warn about.
 */
export function findMissingSections(
  input: CompletenessInput
): MissingSection[] {
  const add = input.addItems;
  const out: (MissingSection | null)[] = [];

  if (!has(input.constructionType) && !coveredByAddItem(add, "constructionType")) {
    out.push({
      key: "constructionType",
      label: "Construction Type",
      kind: "empty",
      detail: "No construction type selected.",
    });
  }

  if (!coveredByAddItem(add, "decking")) {
    out.push(
      checkPair(
        "decking",
        "Decking",
        has(input.selectedDeckingId),
        qty(input.deckingSqFt),
        "decking type",
        "square footage"
      )
    );
  }

  if (!coveredByAddItem(add, "railing")) {
    out.push(
      checkPair(
        "railing",
        "Railing",
        has(input.selectedRailingId),
        qty(input.railingLf),
        "railing type",
        "linear footage"
      )
    );
  }

  if (!coveredByAddItem(add, "stairs")) {
    out.push(
      checkPair(
        "stairs",
        "Stairs",
        has(input.selectedStairsId),
        qty(input.stairsCount),
        "stair type",
        "step count"
      )
    );
  }

  if (!coveredByAddItem(add, "demo")) {
    out.push(
      checkPair(
        "demo",
        "Demo",
        has(input.selectedDemoId),
        qty(input.demoQty),
        "demo type",
        "quantity"
      )
    );
  }

  // Fastener quantity is derived from the decking square footage, so a
  // type on its own is a complete answer here.
  if (!has(input.selectedFastenerId) && !coveredByAddItem(add, "fasteners")) {
    out.push({
      key: "fasteners",
      label: "Fasteners",
      kind: "empty",
      detail: "No fastener type selected.",
    });
  }

  if (!coveredByAddItem(add, "skirting")) {
    out.push(
      checkPair(
        "skirting",
        "Skirting / Lattice",
        has(input.skirtingCategory) || has(input.selectedSkirtingId),
        qty(input.skirtingSf),
        "skirting type",
        "square footage"
      )
    );
  }

  out.push(...findZeroDollarAddItems(add));

  return out.filter((m): m is MissingSection => m !== null);
}

/** Add Items rows where something is picked but the line prices as $0.
 *  Rows without a computed lineBase (unpriced input) are skipped rather
 *  than guessed at. */
function findZeroDollarAddItems(
  addItems: CompletenessInput["addItems"]
): MissingSection[] {
  const out: MissingSection[] = [];
  (addItems || []).forEach((row, idx) => {
    if (!row || typeof row.lineBase !== "number") return;
    const picked =
      has(row.itemId) || has(row.customName) || has((row as any).benchType);
    if (!picked || row.lineBase > 0) return;
    const isMisc = normalizeCat(row.category || "") === "misc";
    const name =
      String(row.displayName || row.customName || "").trim() ||
      String(row.category || "").trim() ||
      "Add item";
    out.push({
      key: `addItem:${row.rowId || idx}`,
      label: `Add Items — ${name}`,
      kind: "partial",
      detail: isMisc
        ? "No price entered — it prices as $0."
        : qty(row.qty)
          ? "This item has no dollar amount — it prices as $0."
          : "An item is selected but the quantity is blank — it prices as $0.",
    });
  });
  return out;
}

/**
 * Stable identity for one set of gaps. The shells remember the signature
 * the user acknowledged so the alert does not re-fire on every trip to
 * the proposal — but DOES fire again once the gaps change (railing filled
 * in, skirting still blank → worth one more look).
 */
export function missingSignature(missing: MissingSection[]): string {
  return missing
    .map((m) => `${m.key}:${m.kind}`)
    .sort()
    .join(",");
}
